import React from 'react';
import { ChevronUp, ChevronDown, ArrowUp, ArrowDown } from 'lucide-react';
import { PageAssignment, PageSectionItem } from '../../hooks/useTwoPhaseRender';
import { SectionData } from '../../hooks/usePdfExportStateV2';
import { SectionRenderer, EditableText } from './SectionRenderer';
import { LogoPlacement, resolveLogoPlacement, BRAND_BAND_HEIGHT_PX } from '../../utils/logoPlacement';

interface PageRendererProps {
  page: PageAssignment;
  sections: SectionData[];
  data: any; // UniversalJsonResponse
  contentWidth: number;
  contentHeight: number;
  marginPixels: number;
  pageDimensions: { width: number; height: number };
  totalPages: number;
  logoDataUrl?: string | null;
  logoPosition?: 'left' | 'center' | 'right';
  logoPlacement?: LogoPlacement;
  companyName?: string;
  headerText?: string;
  footerText?: string;
  reportTitle?: string;
  heights: Map<string, number>;
  selectedSectionId?: string | null;
  onSelectSection?: (sectionId: string | null) => void;
  onNudgeSection?: (sectionId: string, direction: 'up' | 'down') => void;
  sectionOffsets?: Map<string, number>;
  isEditMode?: boolean;
  editedContent?: Map<string, string>;
  onContentChange?: (contentPath: string, value: string) => void;
  removedBlocks?: Set<string>;
  onDiscoverBlocks?: (sectionId: string, blocks: import('../../hooks/usePdfExportStateV2').DiscoveredBlock[]) => void;
}

const HEADER_HEIGHT = 96;

export const PageRenderer: React.FC<PageRendererProps> = ({
  page,
  sections,
  data,
  contentWidth,
  contentHeight,
  marginPixels,
  pageDimensions,
  totalPages,
  logoDataUrl,
  logoPosition = 'right',
  logoPlacement,
  companyName,
  headerText,
  footerText,
  reportTitle,
  heights,
  selectedSectionId,
  onSelectSection,
  onNudgeSection,
  sectionOffsets,
  isEditMode,
  editedContent,
  onContentChange,
  removedBlocks,
  onDiscoverBlocks
}) => {
  const isFirstPage = page.pageNumber === 1;
  // Reserve header space if we have logo or title on first page
  const hasHeader = isFirstPage && (logoDataUrl || reportTitle);
  const headerReservedHeight = hasHeader ? HEADER_HEIGHT : 0;
  const availableContentHeight = contentHeight - headerReservedHeight;

  // Find section data by ID
  const getSectionById = (sectionId: string): SectionData | undefined => {
    return sections.find(s => s.id === sectionId);
  };

  const handleSectionClick = (sectionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectSection?.(sectionId);
  };

  const handlePageClick = () => {
    onSelectSection?.(null);
  };

  return (
    <div
      data-page={page.pageNumber}
      className="pdf-page bg-white shadow-2xl relative"
      style={{
        width: pageDimensions.width,
        height: pageDimensions.height,
        overflow: 'hidden',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.1)'
      }}
      onClick={handlePageClick}
    >
      {/* Margin indicators - visual guides */}
      <div
        className="absolute pointer-events-none z-10"
        style={{
          top: marginPixels,
          left: marginPixels,
          right: marginPixels,
          bottom: marginPixels,
          border: '1px dashed rgba(25, 147, 229, 0.3)',
          borderRadius: 2
        }}
      />

      {/* Content area */}
      <div
        className="pdf-page-content absolute"
        style={{
          top: marginPixels,
          left: marginPixels,
          width: contentWidth,
          height: contentHeight,
          overflow: 'hidden' // Clip at content boundary to respect margins
        }}
      >
        {/* Header on first page - title and logo */}
        {isFirstPage && (
          <div
            className="pdf-page-header pb-4 mb-4 border-b border-gray-200"
            style={{ width: contentWidth, minHeight: HEADER_HEIGHT - 16 }}
          >
            {/* Logo band — absolutely positioned per free placement (x/y/width %) */}
            {logoDataUrl && (
              <div
                className="relative w-full mb-2"
                style={{ height: BRAND_BAND_HEIGHT_PX }}
              >
                {(() => {
                  const p = logoPlacement || resolveLogoPlacement({ logoPosition });
                  return (
                    <img
                      src={logoDataUrl}
                      alt="Company Logo"
                      className="absolute object-contain"
                      style={{
                        left: `${p.x}%`,
                        top: `${p.y}%`,
                        width: `${p.width}%`,
                        maxHeight: BRAND_BAND_HEIGHT_PX,
                        height: 'auto',
                      }}
                    />
                  );
                })()}
              </div>
            )}
            {/* Report title */}
            {reportTitle && (
              <EditableText
                contentPath="report.title"
                originalValue={reportTitle}
                editedContent={editedContent}
                onContentChange={onContentChange}
                isEditMode={isEditMode}
                as="h1"
                className="text-2xl font-bold text-gray-800 text-center mt-2"
              />
            )}
          </div>
        )}

        {/* Section content area */}
        <div
          className="pdf-page-sections relative"
          style={{
            height: availableContentHeight,
            overflow: 'hidden', // Clip content at boundary - nudge controls are inside sections now
            marginTop: headerReservedHeight > 0 ? 0 : undefined
          }}
        >
          {page.sections.map((item: PageSectionItem) => {
            const section = getSectionById(item.sectionId);
            if (!section) return null;

            const sectionHeight = heights.get(section.id) || item.height;
            const isSelected = selectedSectionId === section.id;
            const currentOffset = sectionOffsets?.get(section.id) || 0;
            const clipTop = item.clipTop || 0;
            const visibleHeight = item.visibleHeight || sectionHeight;

            return (
              <div
                key={`${item.sectionId}-${page.pageNumber}-${clipTop}`}
                className={`pdf-section-wrapper absolute left-0 right-0 cursor-pointer transition-all ${
                  isSelected && !item.isContinuation ? 'ring-2 ring-blue-500 ring-offset-2 z-20' : 'hover:ring-1 hover:ring-blue-300'
                }`}
                style={{
                  top: item.offsetY,
                  height: visibleHeight,
                  overflow: 'hidden' // Clip the section content
                }}
                onClick={(e) => handleSectionClick(section.id, e)}
              >
                {/* Inner container with negative margin to show correct portion */}
                <div style={{ marginTop: -clipTop }}>
                  <SectionRenderer
                    section={section}
                    data={data}
                    contentWidth={contentWidth}
                    isEditMode={isEditMode}
                    editedContent={editedContent}
                    onContentChange={onContentChange}
                    removedBlocks={removedBlocks}
                    onDiscoverBlocks={onDiscoverBlocks}
                  />
                </div>

                {/* Continuation indicator */}
                {item.isContinuation && (
                  <div className="absolute top-0 left-0 right-0 bg-blue-100 text-blue-700 text-xs px-2 py-0.5 border-b border-blue-200">
                    {section.title} (continued)
                  </div>
                )}

                {/* Overflow indicator */}
                {item.isOverflowing && (
                  <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-yellow-50 to-transparent pointer-events-none flex items-end justify-center pb-1">
                    <span className="text-xs text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded">
                      Content exceeds page height
                    </span>
                  </div>
                )}

                {/* Selection controls - positioned inside the section */}
                {isSelected && onNudgeSection && (
                  <div className="absolute right-2 top-2 flex flex-row gap-1 z-30 bg-white/90 rounded-lg p-1 shadow-lg border border-blue-200">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onNudgeSection(section.id, 'up');
                      }}
                      className="p-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                      title="Move section up (reduce spacing)"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <div className="text-xs flex items-center text-gray-600 px-2 font-medium">
                      {currentOffset > 0 ? `+${currentOffset}px` : currentOffset === 0 ? '0px' : `${currentOffset}px`}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onNudgeSection(section.id, 'down');
                      }}
                      className="p-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                      title="Move section down (add spacing)"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Selection indicator label */}
                {isSelected && (
                  <div className="absolute -left-2 top-0 bg-blue-500 text-white text-xs px-2 py-0.5 rounded-r shadow-lg z-30">
                    Selected
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Page footer */}
      <div
        className="absolute flex items-center justify-between text-xs text-gray-400"
        style={{
          bottom: marginPixels / 3,
          left: marginPixels,
          right: marginPixels
        }}
      >
        <div className="flex gap-2">
          {companyName && <span>{companyName}</span>}
          {footerText && companyName && <span className="text-gray-300">|</span>}
          {footerText && <span>{footerText}</span>}
        </div>
        <span>Page {page.pageNumber} of {totalPages}</span>
      </div>

      {/* Page number badge */}
      <div className="absolute -top-3 -right-3 bg-[#1993e5] text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg z-20">
        {page.pageNumber}
      </div>
    </div>
  );
};

export default PageRenderer;
